import { ENDPOINTS, METHODS } from "@/constants/apiConstants";
import { apiSlice } from "../baseApi";

const authBaseEndpoint = ENDPOINTS.ORDERS;

// Daily Orders API
export const dashboardApi = apiSlice
  .enhanceEndpoints({
    addTagTypes: ["orderFulfillment", "bulkOrders", "cateringOrders"],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getBulkOrders: builder.query({
        query: ({
          page = 1,
          per_page = 10,
          search = "",
          location_id,
          created_from,
          created_to,
        } = {}) => ({
          url: `${authBaseEndpoint}bulk-order`,
          params: {
            page,
            per_page,
            ...(search && { search }),
            ...(location_id && { location_id }),
            ...(created_from && { created_from }),
            ...(created_to && { created_to }),
          },
        }),
        transformResponse: (response) => {
          return response;
        },
        providesTags: () => [{ type: "bulkOrders" }],
        keepUnusedDataFor: 0,
      }),

      getCateringOrders: builder.query({
        query: ({
          page = 1,
          per_page = 10,
          search = "",
          location_id,
          created_from,
          created_to,
        } = {}) => ({
          url: `${authBaseEndpoint}catering-order`,
          params: {
            page,
            per_page,
            ...(search && { search }),
            ...(location_id && { location_id }),
            ...(created_from && { created_from }),
            ...(created_to && { created_to }),
          },
        }),
        transformResponse: (response) => {
          return response;
        },
        providesTags: () => [{ type: "cateringOrders" }],
        keepUnusedDataFor: 0,
      }),

      getBulkArchivedOrders: builder.query({
        query: ({
          page = 1,
          per_page = 10,
          search = "",
          location_id,
          created_from,
          created_to,
        } = {}) => ({
          url: `${authBaseEndpoint}bulk-archived-order`,
          params: {
            page,
            per_page,
            ...(search && { search }),
            ...(location_id && { location_id }),
            ...(created_from && { created_from }),
            ...(created_to && { created_to }),
          },
        }),
        transformResponse: (response) => {
          return response;
        },
        keepUnusedDataFor: 0,
      }),

      getCateringArchivedOrders: builder.query({
        query: ({
          page = 1,
          per_page = 10,
          search = "",
          location_id,
          created_from,
          created_to,
        } = {}) => ({
          url: `${authBaseEndpoint}catering-archived-order`,
          params: {
            page,
            per_page,
            ...(search && { search }),
            ...(location_id && { location_id }),
            ...(created_from && { created_from }),
            ...(created_to && { created_to }),
          },
        }),
        transformResponse: (response) => {
          return response;
        },
        keepUnusedDataFor: 0,
      }),

      getOrderByReference: builder.query({
        query: (payload) => `${authBaseEndpoint}order-by-reference/${payload}`,
        transformResponse: (response) => {
          return response?.data || response;
        },
        keepUnusedDataFor: 0,
      }),

      /* -------------------------------------------------
         CREATE ORDER ENDPOINTS
         ------------------------------------------------- */

      createBulkOrder: builder.mutation({
        query: (requestData) => ({
          url: `${authBaseEndpoint}create-order`,
          method: METHODS.POST,
          body: requestData,
        }),
        invalidatesTags: () => [{ type: "bulkOrders" }],
      }),

      createEventCustomOrder: builder.mutation({
        query: (requestData) => ({
          url: `${authBaseEndpoint}create-event`,
          method: METHODS.POST,
          body: requestData,
        }),
        invalidatesTags: () => [{ type: "cateringEvents" }],
      }),

      createEventPackageOrder: builder.mutation({
        query: (requestData) => ({
          url: `${authBaseEndpoint}create-package`,
          method: METHODS.POST,
          body: requestData,
        }),
        invalidatesTags: () => [{ type: "cateringOrders" }],
      }),

      archiveOrder: builder.mutation({
        query: (requestData) => ({
          url: `${authBaseEndpoint}archive-order/`,
          method: METHODS.POST,
          body: requestData,
        }),
        invalidatesTags: () => [
          { type: "bulkOrders" },
          { type: "cateringOrders" },
        ],
      }),
      /* -------------------------------------------------
        ORDER FULFILMENT ENDPOINTS
         ------------------------------------------------- */

      getFulfillmentByReference: builder.query({
        query: (payload) => `${authBaseEndpoint}fulfilments/${payload}`,
        transformResponse: (response) => {
          return response?.data || response;
        },
        providesTags: () => [{ type: "orderFulfillment" }],
        keepUnusedDataFor: 0,
      }),

      moveToConfirmed: builder.mutation({
        query: (requestData) => ({
          url: `${authBaseEndpoint}confirm-order`,
          method: METHODS.POST,
          body: requestData,
        }),
        invalidatesTags: () => [
          { type: "orderFulfillment" },
          { type: "cateringOrders" },
          { type: "bulkOrders" },
        ],
      }),

      moveToProcessing: builder.mutation({
        query: (requestData) => ({
          url: `${authBaseEndpoint}process-order`,
          method: METHODS.POST,
          body: requestData,
        }),
        invalidatesTags: () => [
          { type: "orderFulfillment" },
          { type: "cateringOrders" },
          { type: "bulkOrders" },
        ],
      }),

      moveToAvailable: builder.mutation({
        query: (requestData) => ({
          url: `${authBaseEndpoint}avail-order`,
          method: METHODS.POST,
          body: requestData,
        }),
        invalidatesTags: () => [
          { type: "orderFulfillment" },
          { type: "cateringOrders" },
          { type: "bulkOrders" },
        ],
      }),

      moveToPickup: builder.mutation({
        query: (requestData) => ({
          url: `${authBaseEndpoint}pick-up-order`,
          method: METHODS.POST,
          body: requestData,
        }),
        invalidatesTags: () => [
          { type: "orderFulfillment" },
          { type: "cateringOrders" },
          { type: "bulkOrders" },
        ],
      }),

      moveToDelivery: builder.mutation({
        query: (requestData) => ({
          url: `${authBaseEndpoint}deliver-order`,
          method: METHODS.POST,
          body: requestData,
        }),
        invalidatesTags: () => [
          { type: "orderFulfillment" },
          { type: "cateringOrders" },
          { type: "bulkOrders" },
        ],
      }),

      rejectOrder: builder.mutation({
        query: (id) => ({
          url: `${authBaseEndpoint}reject-order/${id}`,
          method: METHODS.DELETE,
        }),
        invalidatesTags: () => [
          { type: "orderFulfillment" },
          { type: "cateringOrders" },
          { type: "bulkOrders" },
        ],
      }),
    }),
  });

// Export hooks for usage in components
export const {
  useGetBulkOrdersQuery,
  useGetCateringOrdersQuery,
  useGetBulkArchivedOrdersQuery,
  useGetCateringArchivedOrdersQuery,
  useGetOrderByReferenceQuery,

  // Create Orders
  useCreateBulkOrderMutation,
  useCreateEventCustomOrderMutation,
  useCreateEventPackageOrderMutation,
  useArchiveOrderMutation,

  // Order Fulfillment
  useGetFulfillmentByReferenceQuery,
  useMoveToConfirmedMutation,
  useMoveToProcessingMutation,
  useMoveToAvailableMutation,
  useMoveToPickupMutation,
  useMoveToDeliveryMutation,
  useRejectOrderMutation,
} = dashboardApi;
